import { supabase, isSupabaseConfigured } from '@/lib/supabase-client'
import { showRateLimitToast } from '@/components/RateLimitToast'
import { withAmazonViewAs } from '@/lib/amazon-view-as'

export class ApiError<T = unknown> extends Error {
  status: number
  body?: T

  constructor(message: string, status: number, body?: T) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * Backend enforce modunda ağır endpoint'ler `429 + Retry-After` döndürür.
 * RateLimitError, ApiError'ın alt tipidir — mevcut `instanceof ApiError`
 * yakalayan tüketiciler bozulmadan çalışır, ek olarak `retryAfter` (saniye)
 * bilgisini taşır. Otomatik retry YOK (T-08-01 self-DoS mitigasyonu).
 */
export class RateLimitError<T = unknown> extends ApiError<T> {
  retryAfter: number

  constructor(message: string, retryAfter: number, body?: T) {
    super(message, 429, body)
    this.name = 'RateLimitError'
    this.retryAfter = retryAfter
  }
}

/**
 * Tek-session enforcement (SE-02): backend, oturumun başka bir cihazda
 * geçersiz kılındığını `401 + X-Session-Superseded` header'ıyla bildirir.
 * SessionSupersededError, ApiError alt tipidir — `instanceof ApiError`
 * yakalayan tüketiciler bozulmadan çalışır. Bu hata NORMAL 401'den ayrıdır:
 * refresh-retry DENENMEZ (sonsuz döngü riski, RESEARCH Pitfall 2), kullanıcı
 * kalıcı "başka yerde giriş yapıldı" ekranına yönlendirilir.
 */
export class SessionSupersededError<T = unknown> extends ApiError<T> {
  constructor(message: string, status = 401, body?: T) {
    super(message, status, body)
    this.name = 'SessionSupersededError'
  }
}

/**
 * #1029 günlük ürün hakkı: sunucu sınır aşıldığında `429` ile
 * `{ error: { code: 'serp_quota_exceeded', details: { resets_at, limit } } }` döner.
 * Bu, hız sınırı DEĞİLDİR: "yavaşla" toast'ı gösterilmez, çağıran taraf bu hatayı
 * yakalayıp kendi arayüzünü gösterir. ApiError alt tipidir (`instanceof ApiError` korunur).
 */
export class QuotaExceededError<T = unknown> extends ApiError<T> {
  resetsAt: string | null
  limit: number | null

  constructor(message: string, resetsAt: string | null, limit: number | null, body?: T) {
    super(message, 429, body)
    this.name = 'QuotaExceededError'
    this.resetsAt = resetsAt
    this.limit = limit
  }
}

/**
 * #1029 Free kullanıcı sayısal sınırı: sunucu sınır dolunca `403` ile
 * `{ error: { code: 'free_limit_reached', details: { feature, limit, used } } }` döner.
 * Diğer 403'lerden (ör. `PLAN_REQUIRED`) ayrıdır; ApiError alt tipidir.
 */
export class FreeLimitReachedError<T = unknown> extends ApiError<T> {
  feature: string | null
  limit: number | null
  used: number | null

  constructor(message: string, feature: string | null, limit: number | null, used: number | null, body?: T) {
    super(message, 403, body)
    this.name = 'FreeLimitReachedError'
    this.feature = feature
    this.limit = limit
    this.used = used
  }
}

const FREE_LIMIT_REACHED_CODE = 'free_limit_reached'

/** 403 gövdesi Free sınırı mı? Dolu ise özellik, sınır ve kullanım döner (alanlar güvenli doğrulanır). */
function readFreeLimitReached(
  body: unknown,
): { feature: string | null; limit: number | null; used: number | null } | null {
  if (!body || typeof body !== 'object') return null
  const error = (body as { error?: unknown }).error
  if (!error || typeof error !== 'object') return null
  const { code, details } = error as { code?: unknown; details?: unknown }
  if (code !== FREE_LIMIT_REACHED_CODE) return null
  const d =
    details && typeof details === 'object'
      ? (details as { feature?: unknown; limit?: unknown; used?: unknown })
      : {}
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  return {
    feature: typeof d.feature === 'string' ? d.feature : null,
    limit: num(d.limit),
    used: num(d.used),
  }
}

const SERP_QUOTA_EXCEEDED_CODE = 'serp_quota_exceeded'

/** 429 gövdesi kota aşımı mı? Dolu ise sıfırlanma zamanı ve sınır döner. */
function readQuotaExceeded(body: unknown): { resetsAt: string | null; limit: number | null } | null {
  if (!body || typeof body !== 'object') return null
  const error = (body as { error?: unknown }).error
  if (!error || typeof error !== 'object') return null
  const { code, details } = error as { code?: unknown; details?: unknown }
  if (code !== SERP_QUOTA_EXCEEDED_CODE) return null
  const d = details && typeof details === 'object' ? (details as { resets_at?: unknown; limit?: unknown }) : {}
  return {
    resetsAt: typeof d.resets_at === 'string' ? d.resets_at : null,
    limit: typeof d.limit === 'number' && Number.isFinite(d.limit) ? d.limit : null,
  }
}

const DEFAULT_RETRY_AFTER_SECONDS = 60

/**
 * `Retry-After` header'ını saniyeye çevirir. Header yoksa veya geçersizse
 * makul bir default (60s) döner. IETF: `Retry-After` delta-seconds (sayı) ya da
 * HTTP-date olabilir; rate-limit senaryosunda backend delta-seconds gönderir.
 */
function parseRetryAfter(header: string | null): number {
  if (!header) return DEFAULT_RETRY_AFTER_SECONDS
  const seconds = Number.parseInt(header, 10)
  if (Number.isFinite(seconds) && seconds > 0) return seconds
  return DEFAULT_RETRY_AFTER_SECONDS
}

type ApiFetchOptions = RequestInit & {
  baseUrl?: string
  parseJson?: boolean
}

const DEFAULT_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

const RANK_TRACKER_BASE_URL = (() => {
  const configured = import.meta.env.VITE_RANK_TRACKER_API_BASE_URL
  if (configured && configured.length > 0) {
    return configured
  }
  const normalizedDefault = DEFAULT_BASE_URL.replace(/\/+$/u, '')
  if (normalizedDefault === '') {
    return '/api/rank-tracker'
  }
  return `${normalizedDefault}/rank-tracker`
})()

// Supabase best practice: onAuthStateChange ile session'ı in-memory cache'le.
// Her API isteğinde getSession() çağırmak yerine cache'den oku.
// getSession() expired token'da implicit refresh yapar ve paralel isteklerde race condition riski taşır.
let _cachedAccessToken: string | null = null
let _sessionInitialized = false

// --- Login-register grace penceresi (register-window race) ---
// Yeni login anında, /session/register current'ı BU oturuma yazmadan ÖNCE, diğer
// provider'lar (subscription/user-profile) paralel authenticated query atar. Bu
// query'ler current=ESKİ oturumken guard'a takılıp 401+X-Session-Superseded alır.
// Grace olmasaydı superseded-dalı signOut() ile YENİ giren kullanıcının taze
// oturumunu YOK EDER (→ reload'da /login). Grace penceresinde superseded 401'i
// TRANSİENT say: signOut/flag/dispatch YOK — register birazdan current'ı düzeltecek,
// sonraki query'ler 200 dönecek. login.tsx / auth-callback markLoginGrace() çağırır.
let _loginGraceUntil = 0

/** Login anında çağrılır: sonraki ~ms boyunca superseded-401'i transient say.
 *  15s: yavaş remote login'de (signIn + access-check + register + paralel provider
 *  query'lerinin yerleşmesi) geç dönen yanlış-pozitif superseded'i de kapsar. */
export function markLoginGrace(ms = 15000): void {
  _loginGraceUntil = (typeof performance !== 'undefined' ? performance.now() : 0) + ms
}

function _inLoginGrace(): boolean {
  return (typeof performance !== 'undefined' ? performance.now() : 0) < _loginGraceUntil
}

if (isSupabaseConfigured() && supabase) {
  supabase.auth.onAuthStateChange((_event, session) => {
    _cachedAccessToken = session?.access_token ?? null
    _sessionInitialized = true
  })
}

// Single-flight refresh: eşzamanlı N×401 durumunda tek bir refreshSession() isteği gönderilir.
// supabase-js dahili refreshingDeferred ile ağ seviyesinde zaten tek istek garantili olsa da,
// bu wrapper orchestration fan-out'u temizler ve _cachedAccessToken module-state'iyle tutarlıdır.
let _refreshInFlight: Promise<string | null> | null = null

export async function getAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured() || !supabase) {
    return null
  }
  // Cache doluysa doğrudan dön — sıfır network call.
  // KRİTİK: cache DOLU sayılması için token'ın da NULL OLMAMASI gerekir. Aksi halde
  // yeni login'de (signInWithPassword hemen sonrası, onAuthStateChange SIGNED_IN
  // cache'i güncellemeden ÖNCE) login.tsx registerSession çağırırsa token=null
  // gider → 401 → current-session yazılamaz → last-login-wins bozulur. Null cache'te
  // getSession() fallback'i taze token'ı doğrudan alır (logout ise yine null döner).
  if (_sessionInitialized && _cachedAccessToken) {
    return _cachedAccessToken
  }
  // Cache boş/null → getSession() fallback (ilk yükleme VEYA login-cache yarışı)
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) {
      console.warn('Supabase oturum okunamadi:', error.message)
      return null
    }
    _cachedAccessToken = session?.access_token ?? null
    _sessionInitialized = true
    return _cachedAccessToken
  } catch (err) {
    console.warn('Supabase oturum istegi basarisiz oldu:', err)
    return null
  }
}

// refreshAccessToken: 401 sonrası token'ı zorla yeniler.
// D-01 KRİTİK: token'ı getAccessToken()'dan ALMA (stale cache döndürür).
// refreshSession() dönüşündeki data.session.access_token'ı DOĞRUDAN kullan.
// D-06: signOut/redirect EKLEME — refresh fail sessizce null döndürür.
async function refreshAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured() || !supabase) return null

  // Single-flight: aynı anda gelen N×401 tek bir refreshSession() çağrısıyla karşılanır (D-04)
  if (_refreshInFlight) return _refreshInFlight

  _refreshInFlight = supabase.auth
    .refreshSession()
    .then(({ data, error }) => {
      if (error || !data.session) {
        console.warn('Token refresh başarısız:', error?.message)
        return null
      }
      // D-05 idempotent cache güncelleme: onAuthStateChange TOKEN_REFRESHED async tetiklenebilir;
      // retry çağrısı içindeki getAccessToken() yeni token'ı görsün diye cache'i senkron güncelle.
      // onAuthStateChange zaten bunu yapıyor, bu yazım redundant ama güvenli.
      _cachedAccessToken = data.session.access_token
      return data.session.access_token
    })
    .finally(() => {
      _refreshInFlight = null
    })

  return _refreshInFlight
}

// apiFetch ve apiStream ortak: taban URL'yi mutlak ve sonu "/"suz hâle getirir.
function formatBaseUrl(input: string): string {
  if (/^https?:\/\//i.test(input)) {
    return input.replace(/\/+$/u, '')
  }
  const normalized = input.startsWith('/') ? input : `/${input}`
  return `${window.location.origin}${normalized.replace(/\/+$/u, '')}`
}

/**
 * SE-02 tek-session: superseded-401 işleyicisi (apiFetch ve apiStream ortak).
 * Her zaman fırlatır. Gövde OKUNMAZ; karar header ile verilir (çağıran).
 */
async function failSessionSuperseded(): Promise<never> {
  // Login-register grace: yeni login anında register current'ı yazmadan ÖNCE
  // gelen superseded 401 YANLIŞ-POZİTİFTİR (current daha eski oturumda). Bu
  // pencerede signOut/flag/dispatch YAPMA — aksi halde yeni giren kullanıcının
  // taze oturumu silinir (→ /login). Sessizce transient hata dön: register
  // birazdan current'ı düzeltir, query'yi tüketen taraf refetch eder.
  if (_inLoginGrace()) {
    throw new SessionSupersededError('superseded (login grace — transient)')
  }
  // signOut LOCAL scope — sadece bu (atılan) cihazın oturumu temizlenir;
  // yeni cihaz ETKİLENMEZ (T-02-17). Hata login akışını kırmasın diye yutulur.
  if (isSupabaseConfigured() && supabase) {
    try {
      await supabase.auth.signOut({ scope: 'local' })
    } catch (err) {
      console.warn('[api-client] superseded signOut hatası:', err)
    }
  }
  // Kalıcı logout ekranını tetikleyecek global sinyal (provider dinler).
  // KRİTİK (cold-load race): event fire-and-forget'tir; tam sayfa reload'ında
  // provider'ın window listener'ı henüz bağlanmadan 401 gelirse event KAÇAR ve
  // kullanıcı /login'e düşer. sessionStorage flag'i kalıcıdır → provider mount'ta
  // okur, race olmadan superseded ekranını gösterir. Flag SIGNED_IN'de ve
  // "Tekrar giriş yap"ta temizlenir.
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem('session:superseded', '1')
    } catch (err) {
      console.warn('[api-client] superseded flag yazılamadı:', err)
    }
    window.dispatchEvent(new CustomEvent('session:superseded'))
  }
  throw new SessionSupersededError('Oturum başka bir cihazda açıldı')
}

export async function apiFetch<T = unknown>(path: string, options: ApiFetchOptions = {}, _retried = false): Promise<T> {
  const { baseUrl = DEFAULT_BASE_URL, parseJson = true, headers, ...init } = options

  const base = formatBaseUrl(baseUrl)
  // #947: yönetici salt-okur satıcı görünümünde Amazon sayfası GET uçlarına `viewAs` eklenir (başka yola/yönteme dokunmaz).
  const normalizedPath = withAmazonViewAs(path.startsWith('/') ? path : `/${path}`, init.method)
  const url = `${base}${normalizedPath}`

  const requestHeaders = new Headers(headers)
  requestHeaders.set('Accept', 'application/json')
  if (init.body && !(init.body instanceof FormData)) {
    requestHeaders.set('Content-Type', 'application/json')
  }

  const token = await getAccessToken()
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: requestHeaders,
  })

  // SE-02 tek-session: superseded-401 kontrolü refresh dalından ÖNCE.
  // KRİTİK (T-02-16): bu dalda refresh DENENMEZ — aksi halde superseded-401
  // sonsuz refresh döngüsüne girer. Karar HEADER ile verilir (body OKUNMAZ →
  // bodyUsed guard'ı korunur; Headers.get case-insensitive'dir).
  if (response.status === 401 && response.headers.get('X-Session-Superseded')) {
    return failSessionSuperseded()
  }

  // D-03: Retry kararı status===401 ile BODY OKUNMADAN verilir (body okuma bodyUsed guard'ına takılır).
  // D-06: Sadece 401 retry; 403 (PLAN_REQUIRED) ve diğer 4xx asla.
  // D-02: _retried 3. param — options nesnesine ASLA flag koyma (options ...init olarak fetch'e yayılıyor).
  if (response.status === 401 && !_retried) {
    const freshToken = await refreshAccessToken()
    if (freshToken) {
      // Retry için yeni Headers kur — mevcut header kurma mantığını AYNEN tekrarla
      const retryHeaders = new Headers(headers)
      retryHeaders.set('Accept', 'application/json')
      // FormData edge notu: retry'da init.body aynı referansla yeniden gönderilir.
      // Mevcut çağrılar JSON (güvenli). FormData body stream tüketilmiş Blob riski taşır
      // ancak apiFetch tüketicilerinde FormData kullanan yoktur — edge belgelenir.
      if (init.body && !(init.body instanceof FormData)) {
        retryHeaders.set('Content-Type', 'application/json')
      }
      retryHeaders.set('Authorization', `Bearer ${freshToken}`)
      return apiFetch<T>(path, { ...options, headers: retryHeaders }, true)
    }
    // freshToken null → refresh başarısız; akış mevcut !response.ok bloğuna düşer.
    // ApiError(401) orijinal body ile throw edilir (D-06 — signOut/redirect yok).
  }

  // D-02/D-03 korunur: 429 kararı status ile verilir, body okuma ZORUNLU değil
  // (header yeterli — bodyUsed guard'ına takılmaz). Otomatik retry YOK (T-08-01):
  // kullanıcıya nazik bekleme mesajı gösterilir, sert engel kaba hataya çevrilmez.
  if (response.status === 429) {
    // #1029: gövde ÖNCE okunur; günlük ürün hakkı aşımı hız sınırı değildir (toast YOK).
    let rateLimitBody: unknown = undefined
    const rlContentType = response.headers.get('content-type') ?? ''
    if (parseJson && rlContentType.includes('application/json')) {
      rateLimitBody = await response.json().catch(() => undefined)
    }
    const quota = readQuotaExceeded(rateLimitBody)
    if (quota) {
      throw new QuotaExceededError('Daily product quota exceeded', quota.resetsAt, quota.limit, rateLimitBody)
    }

    const retryAfter = parseRetryAfter(response.headers.get('Retry-After'))
    showRateLimitToast(retryAfter)

    // Backend detayı sızdırma (T-08-02) — body varsa taşınır ama kullanıcıya
    // ham gösterilmez; mesaj sabit ve nazik.
    throw new RateLimitError('Rate limit exceeded', retryAfter, rateLimitBody)
  }

  if (!response.ok) {
    const contentType = response.headers.get('content-type') ?? ''
    let errorBody: unknown = null

    if (parseJson && contentType.includes('application/json')) {
      errorBody = await response.json().catch(() => null)
    } else {
      errorBody = await response.text().catch(() => 'Unknown error')
    }

    if (response.status === 403) {
      const freeLimit = readFreeLimitReached(errorBody)
      if (freeLimit) {
        throw new FreeLimitReachedError(
          'Free limit reached',
          freeLimit.feature,
          freeLimit.limit,
          freeLimit.used,
          errorBody,
        )
      }
    }

    throw new ApiError('API request failed', response.status, errorBody)
  }

  if (!parseJson) {
    return undefined as T
  }

  try {
    return (await response.json()) as T
  } catch {
    throw new ApiError('API yanıtı JSON formatında değil', response.status)
  }
}

export type ApiStreamInit = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  baseUrl?: string
}

/**
 * Akış (SSE) istekleri için: ham `Response` döndürür, gövdeyi TÜKETMEZ.
 *
 * apiFetch ile ortak: Authorization başlığı, 401'de tek-uçuş yenileme + tek
 * tekrar, 401 + X-Session-Superseded'de yenileme DENEMEDEN oturum-devri hatası.
 * apiFetch'ten farklar: `Accept: text/event-stream`; 429 dahil hiçbir yanıtta
 * toast gösterilmez (bütçe/hız sınırı arayüz kartıdır) ve iki farklı 429
 * (budget_exhausted, rate_limited) ayrı kalsın diye `RateLimitError` DEĞİL,
 * gövdeyi (`error.code`, `retry_after`, `resets_at`) taşıyan `ApiError`
 * fırlatılır. Özel (non-simple) istek başlığı eklenmez: çapraz-origin ön
 * uçuş izin listesi sabittir; yeniden deneme süresi ve çift tıklama anahtarı
 * gövdede taşınır (D-58).
 */
export async function apiStream(path: string, init: ApiStreamInit = {}): Promise<Response> {
  return streamAttempt(path, init, false, undefined)
}

async function streamAttempt(
  path: string,
  init: ApiStreamInit,
  retried: boolean,
  freshToken: string | undefined,
): Promise<Response> {
  const { method = 'POST', body, signal, baseUrl = DEFAULT_BASE_URL } = init
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = `${formatBaseUrl(baseUrl)}${normalizedPath}`

  const requestHeaders = new Headers()
  requestHeaders.set('Accept', 'text/event-stream')
  if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json')
  }
  const token = freshToken ?? (await getAccessToken())
  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: requestHeaders,
    signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  // Superseded-401: refresh DENENMEZ (T-02-16); karar header ile, gövde okunmaz.
  if (response.status === 401 && response.headers.get('X-Session-Superseded')) {
    return failSessionSuperseded()
  }

  // Normal 401: tek-uçuş yenileme + tek tekrar (apiFetch ile aynı). Yeni token
  // doğrudan iletilir (önbellek gecikmesine güvenilmez).
  if (response.status === 401 && !retried) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      return streamAttempt(path, init, true, refreshed)
    }
  }

  if (!response.ok) {
    // Gövde okunamazsa (JSON değil, boş) body undefined kalır; ham metin taşınmaz.
    const errorBody: unknown = await response.json().catch(() => undefined)
    throw new ApiError('API request failed', response.status, errorBody)
  }

  return response
}

export type ApiBlobInit = {
  signal?: AbortSignal
  baseUrl?: string
}

// Blob alımında geçici sunucu/ağ geçidi sınırı (429/503): en çok iki yeniden deneme, artan bekleme;
// `Retry-After` varsa ona uyulur (üst sınırlı). 9 görselli açılış prod hız sınırının altında kalsın (W5).
const BLOB_MAX_RETRIES = 2
const BLOB_BACKOFF_MS = [400, 1200]
const BLOB_RETRY_AFTER_CAP_MS = 10_000

function abortError(): Error {
  return new DOMException('The operation was aborted.', 'AbortError')
}

function waitMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(abortError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function blobRetryDelayMs(response: Response, attempt: number): number {
  const header = response.headers.get('Retry-After')
  if (header) {
    const seconds = Number.parseInt(header, 10)
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Math.min(seconds * 1000, BLOB_RETRY_AFTER_CAP_MS)
    }
  }
  return BLOB_BACKOFF_MS[Math.min(attempt, BLOB_BACKOFF_MS.length - 1)]
}

/**
 * İkili (görsel) okuma: `Blob` döndürür. `<img src>` kimlik başlığı taşıyamadığı için görseller
 * bu yoldan alınıp `URL.createObjectURL` ile gösterilir.
 *
 * apiStream ile ortak: Authorization, 401'de tek-uçuş yenileme + tek tekrar, 401 + X-Session-Superseded'de
 * yenileme DENENMEDEN oturum-devri hatası. Koşullu istek (ETag) tarayıcının işidir; istemci kodunda
 * `If-None-Match` ve özel başlık YOKTUR. 429/503'te en çok iki yeniden deneme (artan bekleme,
 * `Retry-After`'a uyulur); toast gösterilmez. Hata gövdesi JSON ise `ApiError.body` taşır.
 */
export async function apiFetchBlob(path: string, init: ApiBlobInit = {}): Promise<Blob> {
  const { signal, baseUrl = DEFAULT_BASE_URL } = init
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = `${formatBaseUrl(baseUrl)}${normalizedPath}`

  let refreshedToken: string | undefined
  let refreshed = false
  let transientRetries = 0

  for (;;) {
    const requestHeaders = new Headers()
    requestHeaders.set('Accept', 'image/*')
    const token = refreshedToken ?? (await getAccessToken())
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
    }

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: requestHeaders,
      signal,
    })

    if (response.status === 401 && response.headers.get('X-Session-Superseded')) {
      return failSessionSuperseded()
    }

    if (response.status === 401 && !refreshed) {
      refreshed = true
      const fresh = await refreshAccessToken()
      if (fresh) {
        refreshedToken = fresh
        continue
      }
    }

    if ((response.status === 429 || response.status === 503) && transientRetries < BLOB_MAX_RETRIES) {
      const delay = blobRetryDelayMs(response, transientRetries)
      transientRetries += 1
      await waitMs(delay, signal)
      continue
    }

    if (!response.ok) {
      const errorBody: unknown = await response.json().catch(() => undefined)
      throw new ApiError('API request failed', response.status, errorBody)
    }

    return response.blob()
  }
}

export type ApiUploadInit = {
  /** 0..100 tam sayı yüzdeler, artan sırada (geri gitmez). */
  onProgress?: (percent: number) => void
  signal?: AbortSignal
  baseUrl?: string
}

type XhrResult = { status: number; supersededHeader: string | null; text: string }

function sendFormData(
  url: string,
  token: string | undefined,
  formData: FormData,
  report: (percent: number) => void,
  signal?: AbortSignal,
): Promise<XhrResult> {
  return new Promise<XhrResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const xhr = new XMLHttpRequest()
    const onAbort = () => xhr.abort()
    const cleanup = () => signal?.removeEventListener('abort', onAbort)

    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.setRequestHeader('Accept', 'application/json')
    // Content-Type ELLE KONMAZ: tarayıcı multipart sınırını kendisi yazar.
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }
    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable && event.total > 0) {
        report(Math.round((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () => {
      cleanup()
      resolve({
        status: xhr.status,
        supersededHeader: xhr.getResponseHeader('X-Session-Superseded'),
        text: xhr.responseText,
      })
    }
    xhr.onerror = () => {
      cleanup()
      reject(new ApiError('Network error', 0))
    }
    xhr.ontimeout = () => {
      cleanup()
      reject(new ApiError('Network error', 0))
    }
    xhr.onabort = () => {
      cleanup()
      reject(abortError())
    }
    signal?.addEventListener('abort', onAbort, { once: true })
    xhr.send(formData)
  })
}

/**
 * Dosya yükleme (multipart, XHR ile gerçek ilerleme). apiFetch ile aynı kimlik kuralları:
 * Authorization, 401'de tek yenileme + AYNI FormData ile tek tekrar, 401 + X-Session-Superseded'de
 * yenileme denenmeden oturum-devri hatası. Toast yok; özel istek başlığı yok.
 */
export async function apiUpload<T = unknown>(
  path: string,
  formData: FormData,
  init: ApiUploadInit = {},
): Promise<T> {
  const { onProgress, signal, baseUrl = DEFAULT_BASE_URL } = init
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = `${formatBaseUrl(baseUrl)}${normalizedPath}`

  let last = -1
  const report = (percent: number) => {
    const clamped = Math.max(0, Math.min(100, percent))
    if (clamped > last) {
      last = clamped
      onProgress?.(clamped)
    }
  }

  let token: string | undefined | null = await getAccessToken()
  let retried = false

  for (;;) {
    const result = await sendFormData(url, token ?? undefined, formData, report, signal)

    if (result.status === 401 && result.supersededHeader) {
      return failSessionSuperseded()
    }

    if (result.status === 401 && !retried) {
      retried = true
      const fresh = await refreshAccessToken()
      if (fresh) {
        token = fresh
        continue
      }
    }

    if (result.status < 200 || result.status >= 300) {
      let errorBody: unknown = undefined
      try {
        errorBody = JSON.parse(result.text)
      } catch {
        errorBody = undefined
      }
      throw new ApiError('API request failed', result.status, errorBody)
    }

    report(100)
    try {
      return JSON.parse(result.text) as T
    } catch {
      throw new ApiError('API yanıtı JSON formatında değil', result.status)
    }
  }
}

export async function rankTrackerFetch<T = unknown>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  return apiFetch<T>(path, {
    ...options,
    baseUrl: options.baseUrl ?? RANK_TRACKER_BASE_URL,
  })
}
